import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Webhook,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { copyToClipboard } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { formatServerDateTime } from "@/lib/server-time";

export const Route = createFileRoute("/_app/api-keys")({
  component: ApiPage,
  head: () => ({
    meta: [
      { title: "API — SmsEmpire" },
      { name: "description", content: "API keys and webhooks for your SmsEmpire account." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type ApiKeyRow = {
  id: number;
  name: string;
  key_id: string;
  masked: string;
  scopes: string[];
  created_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  active: boolean;
};

type WebhookInfo = {
  id: number;
  url: string;
  events: string[];
  enabled: boolean;
  verified: boolean;
  consecutive_failures: number;
  disabled_reason: string | null;
} | null;

type Overview = {
  enabled: boolean;
  reason: string | null;
  reason_message: string | null;
  discount_pct: number;
  discount_pct_email: number;
  keys: ApiKeyRow[];
  max_keys: number;
  webhooks_enabled: boolean;
  webhook: WebhookInfo;
  docs_url: string;
};

type Delivery = {
  id: string;
  event: string;
  status: string;
  attempts: number;
  response_code: number | null;
  error: string | null;
  created_at: string | null;
};

/**
 * A value the server will never show again.
 *
 * Both the API token and the webhook signing secret exist exactly once, in the response
 * that created them: the token is stored only as a peppered hash and the secret is
 * encrypted at rest. If the user closes this dialog without copying, the only remedy is
 * to create a new one — so the dialog says so plainly instead of looking dismissible.
 */
function SecretDialog({
  open,
  onClose,
  title,
  value,
  note,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  value: string;
  note: string;
}) {
  const [copied, setCopied] = useState(false);
  const { t } = useI18n();

  const copy = async () => {
    if (await copyToClipboard(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error(t("apikeys.copy_failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{note}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3">
          <code className="min-w-0 flex-1 break-all font-mono text-xs">{value}</code>
          <Button size="sm" variant="secondary" onClick={copy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t("apikeys.secret_once")}</span>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t("apikeys.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApiPage() {
  const qc = useQueryClient();
  const { t } = useI18n();

  const [keyName, setKeyName] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [secret, setSecret] = useState<{ title: string; value: string; note: string } | null>(null);

  const overview = useQuery<Overview>({
    queryKey: ["api-overview"],
    queryFn: () => api.get<Overview>("/api/users/me/api"),
  });

  const deliveries = useQuery<{ data: Delivery[] }>({
    queryKey: ["api-deliveries"],
    queryFn: () => api.get<{ data: Delivery[] }>("/api/users/me/api-webhook/deliveries?limit=20"),
    enabled: Boolean(overview.data?.webhook),
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["api-overview"] });
    qc.invalidateQueries({ queryKey: ["api-deliveries"] });
  };

  const fail = (e: unknown) => toast.error(e instanceof ApiError ? e.message : t("common.error"));

  const createKey = useMutation({
    mutationFn: () =>
      api.post<{ token: string }>("/api/users/me/api-keys", { name: keyName || "default" }),
    onSuccess: (data) => {
      setKeyName("");
      setSecret({
        title: t("apikeys.new_key_title"),
        value: data.token,
        note: t("apikeys.new_key_note"),
      });
      invalidate();
    },
    onError: fail,
  });

  const revokeKey = useMutation({
    mutationFn: (id: number) => api.delete(`/api/users/me/api-keys/${id}`),
    onSuccess: () => {
      toast.success(t("apikeys.key_revoked"));
      invalidate();
    },
    onError: fail,
  });

  const saveWebhook = useMutation({
    mutationFn: () =>
      api.put<{ secret?: string }>("/api/users/me/api-webhook", { url: webhookUrl }),
    onSuccess: (data) => {
      if (data.secret) {
        setSecret({
          title: t("apikeys.webhook_secret_title"),
          value: data.secret,
          note: t("apikeys.webhook_secret_note"),
        });
      } else {
        toast.success(t("apikeys.webhook_saved"));
      }
      invalidate();
    },
    onError: fail,
  });

  const rotateSecret = useMutation({
    mutationFn: () => api.post<{ secret: string }>("/api/users/me/api-webhook/rotate-secret"),
    onSuccess: (data) => {
      setSecret({
        title: t("apikeys.rotated_secret_title"),
        value: data.secret,
        note: t("apikeys.rotated_secret_note"),
      });
      invalidate();
    },
    onError: fail,
  });

  const testWebhook = useMutation({
    mutationFn: () => api.post("/api/users/me/api-webhook/test"),
    onSuccess: () => {
      toast.success(t("apikeys.test_queued"));
      invalidate();
    },
    onError: fail,
  });

  const deleteWebhook = useMutation({
    mutationFn: () => api.delete("/api/users/me/api-webhook"),
    onSuccess: () => {
      setWebhookUrl("");
      toast.success(t("apikeys.webhook_removed"));
      invalidate();
    },
    onError: fail,
  });

  const data = overview.data;
  const webhook = data?.webhook ?? null;

  // Revoked keys are never deleted: `activations.api_key_id` has to still name the key
  // that bought something months later, when a charge is disputed. So they are hidden
  // rather than removed — otherwise every rotation leaves another dead row on screen.
  const allKeys = data?.keys ?? [];
  const activeKeys = allKeys.filter((k) => k.active);
  const revokedCount = allKeys.length - activeKeys.length;
  const visibleKeys = showRevoked ? allKeys : activeKeys;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">API</h1>
        <p className="text-sm text-muted-foreground">{t("apikeys.subtitle")}</p>
      </header>

      {data && !data.enabled && (
        <Card className="border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">{t("apikeys.not_available")}</p>
              <p className="text-muted-foreground">{data.reason_message}</p>
            </div>
          </div>
        </Card>
      )}

      {data?.enabled && data.discount_pct > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/10 p-4">
          <p className="text-sm">
            {t("apikeys.discount", { pct: data.discount_pct })}
            {data.discount_pct_email !== data.discount_pct &&
              ` ${t("apikeys.discount_email", { pct: data.discount_pct_email })}`}
            . {t("apikeys.discount_price_note")} <code>GET /api/v1/prices</code>
          </p>
        </Card>
      )}

      {/* ── Keys ─────────────────────────────────────────────────────────── */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">{t("apikeys.keys_title")}</h2>
          </div>
          <a
            href="/api/v1/docs"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            {t("apikeys.reference")} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row">
          <div className="flex-1">
            <Label htmlFor="keyname" className="sr-only">
              {t("apikeys.key_name")}
            </Label>
            <Input
              id="keyname"
              placeholder={t("apikeys.key_name_ph")}
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              maxLength={60}
              disabled={!data?.enabled}
            />
          </div>
          <Button
            onClick={() => createKey.mutate()}
            disabled={!data?.enabled || createKey.isPending}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t("apikeys.create_key")}
          </Button>
        </div>

        {visibleKeys.length ? (
          <div className="space-y-2">
            {visibleKeys.map((key) => (
              <div
                key={key.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{key.name}</span>
                    {!key.active && (
                      <span className="rounded border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-[11px] text-destructive">
                        {t("apikeys.revoked")}
                      </span>
                    )}
                  </div>
                  <code className="font-mono text-xs text-muted-foreground">{key.masked}</code>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {key.last_used_at
                      ? t("apikeys.last_used", { date: formatServerDateTime(key.last_used_at) })
                      : t("apikeys.never_used")}
                    {key.last_used_ip ? ` · ${t("apikeys.from_ip", { ip: key.last_used_ip })}` : ""}
                  </p>
                </div>
                {key.active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => revokeKey.mutate(key.id)}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    {t("apikeys.revoke")}
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {allKeys.length ? t("apikeys.no_active_keys") : t("apikeys.no_keys")}
          </p>
        )}

        {revokedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowRevoked((v) => !v)}
            className="mt-3 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {showRevoked
              ? t("apikeys.hide_revoked")
              : t("apikeys.show_revoked", { count: revokedCount })}
          </button>
        )}
      </Card>

      {/* ── Webhook ──────────────────────────────────────────────────────── */}
      <Card className="p-5">
        <div className="mb-1 flex items-center gap-2">
          <Webhook className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Webhook</h2>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">{t("apikeys.webhook_desc")}</p>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder={t("apikeys.webhook_url_ph")}
            value={webhookUrl || webhook?.url || ""}
            onChange={(e) => setWebhookUrl(e.target.value)}
            disabled={!data?.enabled || !data?.webhooks_enabled}
          />
          <Button
            onClick={() => saveWebhook.mutate()}
            disabled={!data?.enabled || !data?.webhooks_enabled || saveWebhook.isPending}
          >
            {t("apikeys.save")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("apikeys.webhook_rules")}</p>

        {webhook && (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span
                className={
                  webhook.verified
                    ? "rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-600 dark:text-emerald-400"
                    : "rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-amber-600 dark:text-amber-400"
                }
              >
                {webhook.verified ? t("apikeys.verified") : t("apikeys.awaiting_verification")}
              </span>
              {!webhook.enabled && (
                <span className="rounded border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-destructive">
                  {t("apikeys.disabled_reason", { reason: webhook.disabled_reason ?? "" })}
                </span>
              )}
              {webhook.consecutive_failures > 0 && (
                <span className="text-muted-foreground">
                  {t("apikeys.consecutive_failures", { count: webhook.consecutive_failures })}
                </span>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => testWebhook.mutate()}>
                <Send className="mr-1.5 h-4 w-4" />
                {t("apikeys.send_test")}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => rotateSecret.mutate()}>
                <RefreshCw className="mr-1.5 h-4 w-4" />
                {t("apikeys.rotate_secret")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => deleteWebhook.mutate()}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                {t("apikeys.remove")}
              </Button>
            </div>

            <div className="mt-5">
              <h3 className="mb-2 text-sm font-medium">{t("apikeys.recent_deliveries")}</h3>
              {deliveries.data?.data.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="py-1.5 pr-3 font-medium">{t("apikeys.col_event")}</th>
                        <th className="py-1.5 pr-3 font-medium">{t("apikeys.col_status")}</th>
                        <th className="py-1.5 pr-3 font-medium">HTTP</th>
                        <th className="py-1.5 pr-3 font-medium">{t("apikeys.col_tries")}</th>
                        <th className="py-1.5 font-medium">{t("apikeys.col_when")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.data.data.map((d) => (
                        <tr key={d.id} className="border-t border-border">
                          <td className="py-1.5 pr-3 font-mono">{d.event}</td>
                          <td className="py-1.5 pr-3">
                            <span
                              className={
                                d.status === "delivered"
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : d.status === "dead"
                                    ? "text-destructive"
                                    : "text-amber-600 dark:text-amber-400"
                              }
                            >
                              {d.status}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3">{d.response_code ?? d.error ?? "—"}</td>
                          <td className="py-1.5 pr-3">{d.attempts}</td>
                          <td className="py-1.5 text-muted-foreground">
                            {formatServerDateTime(d.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("apikeys.no_deliveries")}</p>
              )}
            </div>
          </>
        )}
      </Card>

      <SecretDialog
        open={secret !== null}
        onClose={() => setSecret(null)}
        title={secret?.title ?? ""}
        value={secret?.value ?? ""}
        note={secret?.note ?? ""}
      />
    </div>
  );
}
